import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Autocomplete,
  useMediaQuery,
  useTheme,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
} from "@mui/material";
import { Icon } from "../../icons";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
const EditCreditsModal = ({
  open,
  onClose,
  client: initialClient = null,
  onUpdate,
  showToast,
}) => {
  const { token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [selectedClient, setSelectedClient] = useState(initialClient);
  const [customCredits, setCustomCredits] = useState("");
  const [planType, setPlanType] = useState("NORMAL");
  const [adminComment, setAdminComment] = useState("");

  const [clientsList, setClientsList] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [loading, setLoading] = useState(false);

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: isMobile ? 40 : 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.PRIMARY,
      },
    },
    "& input, & textarea": {
      color: COLORS.PRIMARY,
      fontWeight: 500,
      fontSize: isMobile ? "14px" : "16px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
      fontSize: isMobile ? "14px" : "16px",
    },
  };

  const disabledInputSx = {
    ...inputSx,
    "& .MuiOutlinedInput-root": {
      height: isMobile ? 40 : 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#E0E0E0",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#E0E0E0 !important",
      },
    },
    "& input, & textarea": {
      color: "#B0B0B0",
      fontWeight: 500,
      fontSize: isMobile ? "14px" : "16px",
    },
  };

  const prefilledAmountSx = {
    "& .MuiOutlinedInput-root": {
      height: isMobile ? 40 : 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.PRIMARY,
      },
      "&.Mui-disabled": {
        "& fieldset": {
          borderColor: COLORS.PRIMARY,
          backgroundColor: "rgba(25, 118, 210, 0.04)",
        },
        "& input": {
          color: COLORS.PRIMARY,
          fontWeight: 500,
          "-webkit-text-fill-color": COLORS.PRIMARY + " !important",
        },
      },
    },
    "& input": {
      color: COLORS.PRIMARY,
      fontWeight: 500,
      fontSize: isMobile ? "14px" : "16px",
    },
  };

  const amountFieldNoClientSx = {
    "& .MuiOutlinedInput-root": {
      height: isMobile ? 40 : 50,
      borderRadius: 1,
      "& fieldset": {
        borderColor: "#E0E0E0",
      },
      "&:hover fieldset": {
        borderColor: "#E0E0E0",
      },
    },
    "& input": {
      color: "#B0B0B0",
      fontWeight: 500,
      fontSize: isMobile ? "14px" : "16px",
    },
  };

  const fetchApprovedClients = async () => {
    try {
      setLoadingClients(true);
      const response = await axios.get(`${url}/api/users/fetch`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          role: "CLIENT",
          limit: 1000,
        },
      });

      if (response.data?.users) {
        setClientsList(
          response.data.users.filter(
            u =>
              u.clientRole?.roleType !== "Individual" &&
              u.status !== "PENDING" &&
              u.status !== "REJECTED"
          )
        );
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
      if (showToast) {
        showToast("error", "Error", "Failed to load clients");
      }
      setClientsList([]);
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (initialClient) {
      setSelectedClient(initialClient);
    }
  }, [initialClient]);

  useEffect(() => {
    if (open) {
      fetchApprovedClients();
      if (!initialClient) {
        setSelectedClient(null);
        setCustomCredits("");
      }
      setPlanType("NORMAL");
      setAdminComment("");
    }
  }, [open, initialClient]);

  // Set the default custom credits when client or plan type changes, OR when modal opens
  useEffect(() => {
    if (open && selectedClient) {
      const activePlan =
        planType === "OUTSTATION"
          ? selectedClient.credits?.outstationCredits?.activePlan
          : selectedClient.credits?.activePlan;

      if (activePlan && activePlan.isActivated) {
        setCustomCredits(activePlan.remainingCredits?.toString() || "0");
      } else {
        setCustomCredits("0");
      }
    } else if (!selectedClient) {
      setCustomCredits("");
    }
  }, [selectedClient, planType, open]);

  const calculateRemainingCredits = (client) => {
    if (!client) return { normal: "-", outstation: "-" };

    const used = client?.credits?.usedCredits || 0;
    const remaining = client?.credits?.totalCredits || 0;
    const total = used + remaining;

    const outstationUsed = client?.credits?.outstationCredits?.usedCredits || 0;
    const outstationRemaining =
      client?.credits?.outstationCredits?.totalCredits || 0;
    const outstationTotal = outstationUsed + outstationRemaining;

    return {
      normal: total === 0 && used === 0 ? "-" : `${remaining}/${total}`,
      outstation:
        outstationTotal === 0 && outstationUsed === 0
          ? "-"
          : `${outstationRemaining}/${outstationTotal}`,
    };
  };

  const approvedName = selectedClient?.name || "";

  const getStatusText = (status) => {
    switch (status) {
      case "APPROVED":
        return "Active";
      case "PENDING":
        return "Pending";
      case "REJECTED":
        return "Rejected";
      default:
        return status || "Pending";
    }
  };

  const isUpdateEnabled = () => {
    if (!selectedClient) {
      return false;
    }
    const creditsValid = customCredits !== "" && parseInt(customCredits) >= 0;
    const commentValid = adminComment.trim().length > 0; // Comment is required to track edits
    return creditsValid && commentValid;
  };

  const handleUpdate = async () => {
    try {
      setLoading(true);

      if (!selectedClient) {
        if (showToast) {
          showToast("error", "Error", "Please select a client");
        }
        setLoading(false);
        return;
      }

      const newTotalCredits = parseInt(customCredits);
      if (isNaN(newTotalCredits) || newTotalCredits < 0) {
        if (showToast) {
          showToast(
            "error",
            "Error",
            "Please enter a valid non-negative number of credits",
          );
        }
        setLoading(false);
        return;
      }

      if (!adminComment.trim()) {
        if (showToast) {
          showToast(
            "error",
            "Comment Required",
            "Please provide a reason for editing credits",
          );
        }
        setLoading(false);
        return;
      }

      let requestBody = {
        clientID: selectedClient._id,
        planType,
        newTotalCredits,
        adminComment,
      };

      const response = await axios.put(
        `${url}/api/admin/force-update-credits`,
        requestBody,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (response.status === 200) {
        if (onUpdate) {
          onUpdate(selectedClient, "updated", newTotalCredits, adminComment);
        }

        if (showToast) {
          showToast(
            "success",
            `Credits Updated`,
            `${selectedClient.name}'s active ${planType} plan is now set to ${newTotalCredits} credits.`,
          );
        }

        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      console.error("Error updating credits:", err);
      if (showToast) {
        showToast(
          "error",
          "Error",
          err.response?.data?.message || "Failed to update credits",
        );
      }
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <>
      <Box
        onClick={onClose}
        sx={{
          position: "fixed",
          inset: 0,
          bgcolor: "rgba(0,0,0,0.5)",
          zIndex: 1400,
        }}
      />

      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: isMobile ? "90vw" : isTablet ? "85vw" : 730,
          maxWidth: "95vw",
          maxHeight: "90vh",
          overflowY: "auto",
          bgcolor: "#fff",
          borderRadius: 2,
          p: isMobile ? 2 : 3,
          zIndex: 1401,
          boxShadow: "0px 12px 30px rgba(0,0,0,0.2)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: isMobile ? 2 : 3,
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography
            sx={{
              ...TYPOGRAPHY.H3,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? "1.25rem" : TYPOGRAPHY.H3.fontSize,
            }}
          >
            Edit Client Credits:
          </Typography>
          <IconButton onClick={onClose} sx={{ p: 1 }}>
            <Icon name="close" color={COLORS.ERROR} />
          </IconButton>
        </Box>

        {/* Credit Type Selection */}
        <Box sx={{ mb: isMobile ? 2 : 3 }}>
          <Typography
            sx={{
              ...TYPOGRAPHY.H4,
              color: COLORS.PRIMARY,
              fontSize: isMobile ? "0.875rem" : TYPOGRAPHY.H4.fontSize,
              mb: 1,
            }}
          >
            Select Credit Type
          </Typography>
          <RadioGroup
            row
            value={planType}
            onChange={(e) => {
              setPlanType(e.target.value);
            }}
          >
            <FormControlLabel
              value="NORMAL"
              control={
                <Radio
                  sx={{
                    color: COLORS.PRIMARY,
                    "&.Mui-focused": { color: COLORS.PRIMARY },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: COLORS.TEXT,
                  }}
                >
                  Normal Credits
                </Typography>
              }
            />
            <FormControlLabel
              value="OUTSTATION"
              control={
                <Radio
                  sx={{
                    color: COLORS.PRIMARY,
                    "&.Mui-focused": { color: COLORS.PRIMARY },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: isMobile ? "13px" : "14px",
                    color: COLORS.TEXT,
                  }}
                >
                  Outstation Credits
                </Typography>
              }
            />
          </RadioGroup>
          {planType === "OUTSTATION" && (
            <Typography
              sx={{
                fontSize: isMobile ? "11px" : "13px",
                color: COLORS.SECONDARY,
                mt: 0.5,
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Pricing: ₹8,000 per day + transportation + food per credit
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: isMobile ? 1 : 2,
            mb: isMobile ? 2 : 3,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {/* Search Client - Show dropdown only when no initial client is passed */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minWidth: isMobile ? "100%" : "auto",
            }}
          >
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                color: COLORS.PRIMARY,
                fontSize: isMobile ? "0.875rem" : TYPOGRAPHY.H4.fontSize,
              }}
            >
              Search Client by Name
            </Typography>

            {initialClient ? (
              // Show only the client name when passed from edit icon
              <TextField
                value={selectedClient?.name || ""}
                variant="outlined"
                size="small"
                disabled
                sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root": {
                    height: isMobile ? 40 : 50,
                    "& fieldset": {
                      borderColor: COLORS.PRIMARY,
                    },
                  },
                }}
                InputProps={{
                  readOnly: true,
                  sx: {
                    color: COLORS.PRIMARY,
                    fontWeight: 500,
                  },
                }}
              />
            ) : (
              // Show dropdown when no client is passed (for Add Credits scenario)
              <Autocomplete
                value={selectedClient}
                onChange={(event, newValue) => {
                  setSelectedClient(newValue);
                  setCustomCredits("");
                }}
                options={clientsList}
                getOptionLabel={(option) => option?.name || ""}
                loading={loadingClients}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search client by name"
                    variant="outlined"
                    size="small"
                    sx={inputSx}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingClients ? (
                            <CircularProgress size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option._id}>
                    <Box sx={{ width: "100%", py: 0.5 }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          fontSize: isMobile ? "13px" : "14px",
                        }}
                      >
                        {option.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: isMobile ? "11px" : "12px",
                          color: COLORS.LIGHT_SECONDARY,
                        }}
                      >
                        {option.email}
                      </Typography>
                    </Box>
                  </Box>
                )}
                noOptionsText="No clients found"
                isOptionEqualToValue={(option, value) =>
                  option._id === value?._id
                }
                componentsProps={{
                  popper: {
                    sx: {
                      zIndex: 9999,
                    },
                  },
                }}
              />
            )}
          </Box>

          {/* Edit Credits Input */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              flex: 1,
              minWidth: isMobile ? "100%" : "auto",
            }}
          >
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                color: COLORS.PRIMARY,
                fontSize: isMobile ? "0.875rem" : TYPOGRAPHY.H4.fontSize,
              }}
            >
              {planType === "NORMAL"
                ? "Active Normal Credits*"
                : "Active Outstation Credits*"}
            </Typography>
            <TextField
              type="number"
              value={customCredits}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || /^\d+$/.test(val)) {
                  setCustomCredits(val);
                }
              }}
              disabled={!selectedClient || loading}
              placeholder="Enter active credits"
              size="small"
              sx={!selectedClient ? disabledInputSx : inputSx}
            />
          </Box>
        </Box>

        {/* Comment Field (Mandatory for deductions) */}
        {selectedClient && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              mb: isMobile ? 2 : 3,
            }}
          >
            <Typography
              sx={{
                ...TYPOGRAPHY.H4,
                color: COLORS.PRIMARY,
                fontSize: isMobile ? "0.875rem" : TYPOGRAPHY.H4.fontSize,
              }}
            >
              Admin Comment*
            </Typography>
            <TextField
              value={adminComment}
              onChange={(e) => setAdminComment(e.target.value)}
              placeholder={"Reason for credit edit is mandatory"}
              variant="outlined"
              size="small"
              multiline
              minRows={2}
              fullWidth
              sx={{
                ...inputSx,
                "& .MuiOutlinedInput-root": {
                  ...inputSx["& .MuiOutlinedInput-root"],
                  height: "auto",
                  minHeight: 50,
                  py: 1.5,
                },
              }}
            />
          </Box>
        )}

        {/* Client Info Section */}
        {selectedClient && (
          <>
            <Divider
              sx={{
                mb: isMobile ? 2 : 3,
                borderColor: COLORS.LIGHT_SECONDARY,
              }}
            />

            <Box sx={{ mb: isMobile ? 2 : 3 }}>
              <Box>
                {/* Top Row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: isMobile ? "flex-start" : "center",
                    mb: 1,
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? 1 : 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Icon name="profile" color={COLORS.PRIMARY} />
                    <Typography
                      component="div"
                      sx={{
                        ...TYPOGRAPHY.H3,
                        color: COLORS.TEXT,
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 1,
                        fontSize: isMobile ? "1rem" : TYPOGRAPHY.H3.fontSize,
                        flexWrap: "wrap",
                      }}
                    >
                      {selectedClient.name}
                      <Chip
                        label={getStatusText(selectedClient.status)}
                        size="small"
                        sx={{
                          bgcolor:
                            selectedClient.status === "APPROVED"
                              ? "#8CD47E"
                              : selectedClient.status === "REJECTED"
                                ? "#FF6961"
                                : "#F8D66D",
                          color: "#fff",
                          height: 22,
                          ...TYPOGRAPHY.BODY_SMALL,
                          fontSize: isMobile
                            ? "0.75rem"
                            : TYPOGRAPHY.BODY_SMALL.fontSize,
                        }}
                      />
                    </Typography>
                  </Box>

                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_LARGE,
                      color: COLORS.TEXT,
                      fontSize: isMobile
                        ? "0.875rem"
                        : TYPOGRAPHY.BODY_LARGE.fontSize,
                    }}
                  >
                    Joined on:{" "}
                    {new Date(
                      selectedClient.joinedAt || selectedClient.createdAt,
                    )
                      .toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "2-digit",
                      })
                      .replace(/\//g, "/")}
                  </Typography>
                </Box>

                {/* Email */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                    pl: isMobile ? 0 : "24px",
                    flexWrap: "wrap",
                  }}
                >
                  <Icon
                    name="at"
                    size={isMobile ? 14 : 16}
                    color={COLORS.SECONDARY}
                  />
                  <Typography
                    sx={{
                      ...TYPOGRAPHY.BODY_STANDARD,
                      color: COLORS.SECONDARY,
                      fontSize: isMobile
                        ? "0.875rem"
                        : TYPOGRAPHY.BODY_STANDARD.fontSize,
                    }}
                  >
                    {selectedClient.email}
                  </Typography>
                </Box>

                {/* Phone + Credits */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    pl: isMobile ? 0 : "24px",
                    flexDirection: isMobile ? "column" : "row",
                    alignItems: isMobile ? "flex-start" : "center",
                    gap: isMobile ? 1 : 0,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Icon
                      name="phoneDone"
                      size={isMobile ? 14 : 16}
                      color={COLORS.SECONDARY}
                    />
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_STANDARD,
                        color: COLORS.SECONDARY,
                        fontSize: isMobile
                          ? "0.875rem"
                          : TYPOGRAPHY.BODY_STANDARD.fontSize,
                      }}
                    >
                      {selectedClient.phone}
                    </Typography>
                  </Box>

                  {/* Credits (Right Side) - Updated format to show both */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: isMobile ? "flex-start" : "flex-end",
                      mt: isMobile ? 1 : 0,
                    }}
                  >
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_STANDARD,
                        color: COLORS.SECONDARY,
                        fontSize: isMobile
                          ? "0.875rem"
                          : TYPOGRAPHY.BODY_STANDARD.fontSize,
                      }}
                    >
                      Normal Credits:{" "}
                      <strong>
                        {calculateRemainingCredits(selectedClient).normal}
                      </strong>
                    </Typography>
                    <Typography
                      sx={{
                        ...TYPOGRAPHY.BODY_STANDARD,
                        color: COLORS.SECONDARY,
                        fontSize: isMobile
                          ? "0.875rem"
                          : TYPOGRAPHY.BODY_STANDARD.fontSize,
                      }}
                    >
                      Outstation Credits:{" "}
                      <strong>
                        {calculateRemainingCredits(selectedClient).outstation}
                      </strong>
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Divider
              sx={{
                mb: isMobile ? 2 : 3,
                borderColor: COLORS.LIGHT_SECONDARY,
              }}
            />
          </>
        )}

        {/* Update Button Section */}
        <Box
          sx={{
            display: "flex",
            justifyContent: selectedClient ? "space-between" : "flex-end",
            alignItems: "center",
            gap: 2,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          {selectedClient && (
            <Typography
              sx={{
                ...TYPOGRAPHY.BODY_LARGE,
                color: COLORS.TEXT,
                flex: 1,
                fontSize: isMobile
                  ? "0.875rem"
                  : TYPOGRAPHY.BODY_LARGE.fontSize,
                textAlign: isMobile ? "center" : "left",
                mb: isMobile ? 1 : 0,
              }}
            >
              On Update:{" "}
              <strong style={{ color: COLORS.PRIMARY }}>{customCredits}</strong>{" "}
              Credits will be the new total in{" "}
              <strong style={{ color: COLORS.PRIMARY }}>{approvedName}</strong>
              ’s {planType} plan.
            </Typography>
          )}

          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon name="check" />
              )
            }
            sx={{
              bgcolor: COLORS.PRIMARY,
              "&:hover": { bgcolor: COLORS.PRIMARY },
              minWidth: isMobile ? "100%" : 120,
              fontSize: isMobile ? "0.875rem" : "1rem",
              py: isMobile ? 1 : 1.5,
            }}
            disabled={!isUpdateEnabled() || loading}
            onClick={handleUpdate}
          >
            {loading ? "Updating..." : "Update"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default EditCreditsModal;
